<template>
  <div class="modal-invoice-view-wrap">
    <portal to="dash-modal">
      <!-- only for mark as sent -->
      <InvoiceSendAdjustModal
        v-if="!showInvoicesModal && showAdjustModal && invoice"
        :invoice-id="invoice.id"
        :mark-as-sent="markedAsSent"
        @mark-invoice-as-sent="$emit('mark-as-sent')"
        @close="closeAdjustModal"
        @marked-as-sent="
          () => {
            $emit('invoice-updated')
            showAdjustModal = false
          }
        "
      />

      <!-- Should always be used to inform that eInvoice authorization is required -->
      <EInvoiceMessageModal 
        :showModal="showEInvoiceMessageModal"
        :title="eInvoiceMessageTitle"
        :message="eInvoiceMessageText"
        :actionType="eInvoiceMsgActionType"
        showAnimation="lock"
        @close="onEInvoiceMessageClose"
      />

      <InvoiceDesignModal v-if="showDesign" @layout-updated="refreshPDF" @close="closeDesignModal" />
    </portal>

    <!-- MAIN -->
    <div class="mastermodal-view-wrap">
      <div class="mastermodal-view--left">
        <div v-if="!isRecurrent" class="mastermodal-mobile-status">
          <InvoiceStatusBadge :is-quotation="isQuotation" />
        </div>
        <overlay-scrollbars
          :options="{
            scrollbars: { autoHide: 'never' },
            overflowBehavior: { y: 'scroll', x: 'hidden' },
            className: 'os-theme-dark os-theme-gekko',
          }"
        >
          <div v-if="detailView === 'details'" class="mastermodal-view-tabs--details">
            <div v-if="debug" class="detail-tab--section">
              <div class="detail-tab--section__line text-danger">
                <h4>Debug</h4>
              </div>
              <div class="detail-tab--section__line text-danger">
                <div class="">
                  {{ userAgent }}
                </div>
              </div>
              <div class="detail-tab--section__line text-danger">
                <div>Mobile OS</div>
                <div class="">
                  {{ mobileOS }}
                </div>
              </div>
              <div class="detail-tab--section__line text-danger">
                <div>PDF Viewer</div>
                <div class="">
                  {{ viewerEnabled ? 'Enabled' : 'Disabled' }}
                </div>
              </div>
            </div>

            <div class="detail-tab--section">
              <!-- TOTAL -->
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Total') }}</h4>
                <div class="total-amount" v-html="totalAmount" />
              </div>

              <!-- Number / name -->
              <div v-if="!isRecurrent" class="detail-tab--section__line">
                <div v-if="isQuotation">{{ $gettext('Quotation number') }}</div>
                <div v-else>{{ $gettext('Invoice number') }}</div>
                <div class="strong">
                  {{ invoice.number }}
                </div>
              </div>

              <!-- Date / period -->
              <div class="detail-tab--section__line">
                <div v-if="isQuotation">{{ $gettext('Quotation date') }}</div>
                <div v-else>{{ $gettext('Invoice date') }}</div>
                <div class="">
                  {{ invoice.invoice_date | formatDate('DD-MM-YYYY') }}
                </div>
              </div>
              <div v-if="invoice.status !== 'p' && !invoice.paid_up_front" class="detail-tab--section__line">
                <div v-if="isQuotation">{{ $gettext('Valid until') }}</div>
                <div v-else>{{ $gettext('Payment due') }}</div>
                <div class="">
                  {{ invoice.due_date | formatDate('DD-MM-YYYY') }}
                </div>
              </div>
              <div class="detail-tab--section__line">
                <div>{{ $gettext('Customer') }}</div>
                <div class="">
                  {{ invoice.recipient_customer.name }}
                </div>
              </div>
              <div v-if="invoice.project" class="detail-tab--section__line">
                <div>{{ $gettext('Project') }}</div>
                <div class="">
                  {{ invoice.project.name }}
                </div>
              </div>
            </div>

            <!-- Verifactu checkbox -->
            <div v-if="!isQuotation && currentCompany.country === 'ES'" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <div 
                  class="invoice-verifactu-switch-wrap"
                  :class="{ 'verifactu-active': isVerifactuInvoiceModel }"
                  v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }"
                  :title="verifactuTooltipTitle"
                >
                  <label class="invoice-verifactu-switch-label me-2 mb-0" for="verifactu-switch">
                    <img src="/static/img/verifactu-logo.png">
                  </label>
                  <b-form-checkbox 
                    id="verifactu-switch"
                    :disabled="verifactuCheckboxDisabled"
                    v-model="isVerifactuInvoiceModel" 
                    @change="markAsVerifactu()"
                    class="invoice-verifactu-switch" size="lg" switch
                  />
                </div>
              </div>
              <span style="font-size: 13px;"><a class="text-info" :href="$gettext('/verifactu-declaration')">{{ $gettext('Terms and conditions') }}</a>&nbsp;{{ $gettext('apply') }}</span>

              <div v-if="invoice.verifactuinvoice" class="detail-tab--section__line mt-2">
                <div>{{ $gettext('Verifactu status') }}</div>
                <div v-if="['unmarked', 'unmarked_sent'].includes(invoice.verifactuinvoice.status)" class="text-unassigned">
                  {{ $gettext('Not set') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'draft'" class="text-info">
                  {{ $gettext('Draft') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'correct'" class="text-success">
                  {{ $gettext('Correct') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'partially_correct'" class="text-warning">
                  {{ $gettext('Partially correct') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'incorrect'" class="text-danger">
                  {{ $gettext('Incorrect') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'error'" class="text-danger">
                  {{ $gettext('Error') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'cancelled'" class="text-success">
                  {{ $gettext('Cancelled') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'cancelled_with_errors'" class="text-warning">
                  {{ $gettext('Cancelled with errors') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'cancellation_incorrect'" class="text-danger">
                  {{ $gettext('Cancellation incorrect') }}
                </div>
                <div v-if="invoice.verifactuinvoice.status === 'error_cancelling'" class="text-danger">
                  {{ $gettext('Error cancelling') }}
                </div>
              </div>
              
              <div v-if="!invoice.verifactuinvoice" class="detail-tab--section__line mt-2">
                <div>{{ $gettext('Verifactu status') }}</div>
                <div class="text-unassigned">
                  {{ $gettext('Not set') }}
                </div>
              </div>

              <div v-if="invoice.verifactuinvoice && 
                !['draft', 'unmarked', 'unmarked_sent', 'correct', 'cancelled'].includes(invoice.verifactuinvoice.status)" 
                class="detail-tab--section__line text-unassigned">
                  <i>{{ invoice.verifactuinvoice.status_description }}</i>
              </div>

              <b-alert v-if="isVerifactuInvoiceModel && isVATExempt" show class="gk-alert m-0" :class="showCancelVerifactudButton ? 'mb-2' : ''" style="font-size: 1rem;" variant="warning">
                {{ $gettext('Gekko currently only supports VAT exemptions according to Article 20 of the VAT Directive.') }}
              </b-alert>
              
              <b-alert v-if="isVerifactuInvoiceModel && isCredit" show class="gk-alert m-0" :class="showCancelVerifactudButton ? 'mb-2' : ''" style="font-size: 1rem;" variant="warning">
                {{ $gettext('Gekko currently only supports correction invoices by difference for errors based on law and Article 80, Sections One, Two, and Six of the VAT Law (LIVA).') }}
              </b-alert>

              <GekkoButton
                v-if="showCancelVerifactudButton"
                :text="$gettext('Cancel invoice')"
                icon="fa-regular fa-xmark"
                variant="outline-danger"
                v-gk-confirm="{
                  title: $gettext('Cancel invoice'),
                  message: $gettext('By confirming this action, you will cancel the issued invoice with the tax authorities.'),
                  options: {
                    warning: $gettext('This action cannot be undone.'),
                    variant: 'danger',
                    confirmText: $gettext('Continue')
                  },
                  callback: () => { onVerifactuInvoiceCancelled() }
                }" />
            </div>

            <!-- Peppol checkbox -->
            <div v-if="showPeppolCheckbox" class="detail-tab--section">
              <div class="detail-tab--section__line" style="position: relative;">
                <div 
                  class="invoice-peppol-switch-wrap"
                  :class="{ 'peppol-active': isPeppolInvoiceModel }"
                  v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }"
                  :title="peppolTooltipTitle"
                >
                  <label class="invoice-peppol-switch-label me-2 mb-0" for="peppol-switch">
                    <img src="/static/img/peppol-logo.png" style="width: 46px; height: 46px;">
                    <span>Send via Peppol</span>
                  </label>

                  <b-form-checkbox 
                    id="peppol-switch"
                    :disabled="peppolCheckboxDisabled || loadingPeppolRequest"
                    v-model="isPeppolInvoiceModel" 
                    @change="markAsPeppol()"
                    class="invoice-peppol-switch" size="lg" switch
                  />
                </div>
                <LoadingSpinner v-if="loadingPeppolRequest" />
              </div>

              <span style="font-size: 13px;">
                {{ $gettext('When sending an invoice via Peppol, the Maventa') }}<br/>
                <a class="text-info" href="https://maventa.com/terms-and-conditions#english">{{ $gettext('terms & conditions') }}</a>&nbsp;{{ $gettext('apply. Please check your') }}<br/>
                {{ $gettext('Peppol settings page for more info.') }}
              </span>
              <br/><br/>
              
              <div class="detail-tab--section__line ml-2 mr-2">
                <div :class="loadingPeppolRequest ? 'text-unassigned' : ''">{{ $gettext('Peppol status') }}</div>
                <div v-if="!invoice.peppolinvoice || !invoice.peppolinvoice.status" class="text-unassigned">
                  {{ $gettext('Not set') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'draft'" class="text-info">
                  {{ $gettext('Draft') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'sent'" class="text-warning">
                  {{ $gettext('Sending') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'delivered'" class="text-success">
                  {{ $gettext('Delivered') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'confirmed'" class="text-success">
                  {{ $gettext('Confirmed') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'validation_error'" class="text-danger">
                  {{ $gettext('Invoice validation failed') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'validation_request_failed'" class="text-danger">
                  {{ $gettext('Validation request failed') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'error_sending'" class="text-danger">
                  {{ $gettext('Error sending invoice') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'failed'" class="text-danger">
                  {{ $gettext('Failed') }}
                </div>
                <div v-if="invoice.peppolinvoice && invoice.peppolinvoice.status === 'recipient_not_found'" class="text-danger">
                  {{ $gettext('Invoice recipient not found') }}
                </div>
                <div v-if="invoice.peppolinvoice && ['unknown_webhook_event', 'unknown_sent'].includes(invoice.peppolinvoice.status)" class="text-unassigned">
                  {{ $gettext('Unknown') }}
                </div>
              </div>

              <div v-if="invoice.peppolinvoice && ['validation_error', 'validation_request_failed', 'error_sending'].includes(invoice.peppolinvoice.status)" 
                class="detail-tab--section__line text-unassigned"
                style="white-space: pre-line;">
                  <i>{{ invoice.peppolinvoice.error_message }}</i>
              </div>

              <div v-if="invoice.peppolinvoice && (invoice.peppolinvoice.company_name && invoice.peppolinvoice.business_id)">
                <ul class="list-group">
                  <li class="list-group-item">
                    <strong>{{ $gettext('Company name') }}:</strong> {{ invoice.peppolinvoice.company_name }}
                  </li>
                  <li class="list-group-item">
                    <strong>{{ $gettext('Business ID') }}:</strong> {{ invoice.peppolinvoice.business_id }}
                  </li>
                  <li class="list-group-item">
                    <strong>{{ $gettext('Project reference') }}:</strong> {{ invoice.project_reference }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Schedule summary -->
            <div
              v-if="scheduleSummary"
              @click="$store.commit('invoicesRecurrent/TOGGLE_MODAL')"
              class="detail-tab--section cursor-pointer"
            >
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Scheduled') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <div class="">
                  <b-alert show class="gk-alert mb-0" variant="info">
                    <font-awesome-icon icon="fa-regular fa-clock-rotate-left" />
                    {{ scheduleSummary }}
                  </b-alert>
                </div>
              </div>
            </div>

            <!-- Files -->
            <div v-if="isQuotation && attachedDocuments && attachedDocuments.length > 0" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Files') }}</h4>
              </div>
              <div v-for="file in attachedDocuments" :key="file.id" class="detail-tab--section__line">
                <div>{{ file.title }}</div>
                <div>
                  <GekkoButton
                    @click="downloadDocument(file)"
                    icon="fa-solid fa-download"
                    variant="link"
                    class="text-info p-0"
                    size="sm"
                  />
                </div>
              </div>
            </div>

            <!-- Message -->
            <div v-if="invoice.message && invoice.message != ''" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Message') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <div class="">
                  {{ invoice.message }}
                </div>
              </div>
            </div>

            <!-- History -->
            <div v-if="!isRecurrent" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('History') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <TimelineWidget
                  v-if="invoiceId && !isQuotation"
                  :widget="{ config: { object_type: 'invoice', invoice_id: invoiceId } }"
                />
                <TimelineWidget
                  v-if="invoiceId && isQuotation"
                  :widget="{ config: { object_type: 'quotation', quotation_id: invoiceId } }"
                />
              </div>
            </div>
          </div>
        </overlay-scrollbars>
      </div>

      <div
        class="mastermodal-view--right"
        :class="{ expanded: previewExpanded, 'has-badge': !currentCompany.is_premium }"
      >
        <div class="mastermodal-view--right__handle" @click="previewExpanded = !previewExpanded" />
        <div class="mastermodal-view--right__document">
          <InvoicePDFPreview
            :is-quotation="isQuotation"
            :key="previewPDFId"
            :invoice-id="invoiceId"
            ref="invoice-pdf-preview"
            @show-design="showDesign = true"
          />
        </div>

        <div v-if="!currentCompany.is_premium" class="mastermodal-view--right__poweredby">
          <div @click="goToSubscriptions()" id="tt-premium-powered">
            <GkLogo :full="false" :fill="'brand'" /> {{ $gettext('powered by getgekko.com') }}
          </div>
          <b-tooltip :custom-class="'tooltip-padded'" target="tt-premium-powered" placement="top" triggers="hover">
            <div class="tooltip-title">
              {{ $gettext('Powered by getgekko.com') }}
            </div>
            <div class="tooltip-text">
              {{ $gettext('To remove the Gekko logo on your invoice, upgrade to Premium.') }}
            </div>
          </b-tooltip>
        </div>

        <div v-if="!isRecurrent" class="mastermodal-view--right__statuswrap">
          <InvoiceStatusBadge :is-quotation="isQuotation" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import { promptDownload } from '@/utils/'

import invoices from "@/mixins/invoices";
import pdfviewer from "@/mixins/pdfviewer";
import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import GkLogo from "@/components/ui/GkLogo.vue";
import EInvoiceMessageModal from "@/components/invoices/ui/EInvoiceMessageModal"
import InvoiceSendAdjustModal from "@/components/invoices/ui/InvoiceSendAdjustModal"
import InvoicePDFPreview from "@/components/invoices/ui/InvoicePDFPreview"
import InvoiceDesignModal from "@/components/invoices/ui/InvoiceDesignModal"
import InvoiceStatusBadge from "@/components/invoices/ui/InvoiceStatusBadge"
import TimelineWidget from "@/components/dashboard/widgets/TimelineWidget";
import DocumentService from "@/store/services/document.service";
import InvoiceService from "@/store/services/invoice.service.js"

export default {
  name: 'InvoiceView',
  mixins: [invoices, pdfviewer],
  components: {
    GekkoButton,
    LoadingSpinner,
    GkLogo,
    InvoiceSendAdjustModal,
    InvoicePDFPreview,
    InvoiceDesignModal,
    InvoiceStatusBadge,
    TimelineWidget,
    EInvoiceMessageModal
  },
  props: {
    isQuotation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      invoiceId: null,
      loading: true,
      showInvoicesModal: false,
      showDesign: false,
      showAdjustModal: false,
      modalType: null,
      markedAsSent: false,
      detailView: 'details',
      previewPDFId: null,
      previewExpanded: false,
      showEInvoiceMessageModal: false,
      eInvoiceMessageTitle: '',
      eInvoiceMessageText: '',
      eInvoiceMsgActionType: null,
      debug: false,
      loadingPeppolRequest: false
    }
  },
  computed: {
    ...mapGetters({
      currentUserProfile: 'main/getCurrentUserProfile',
      currentCompany: 'main/getCurrentCompany',
      currentLayout: 'invoicesLayout/getCurrentLayout',
      creditableStatuses: 'invoices/getCreditableStatuses',
      invoice: 'invoices/getInvoice',
      recurrentInvoice: 'invoices/getRecurrentInvoice',
      invoiceToCopy: 'invoices/getInvoiceToCopy',
      invoicePDF: 'invoices/getInvoicePDF',
      currencyByCode: 'main/getCurrencyByCode',
      quotationFiles: 'invoices/getQuotationFiles',
      scheduleSummary: 'invoicesRecurrent/getSummary',
      isVerifactuInvoice: "invoices/getIsVerifactuInvoice",
      isPeppolInvoice: "invoices/getIsPeppolInvoice",
    }),
    isVerifactuInvoiceModel: {
      get() {
        return this.isVerifactuInvoice
      },
      set(value) {
        this.$store.commit('invoices/SET_IS_VERIFACTU_INVOICE', value)
      }
    },
    isPeppolInvoiceModel: {
      get() {
        return this.isPeppolInvoice
      },
      set(value) {
        this.$store.commit('invoices/SET_IS_PEPPOL_INVOICE', value)
      }
    },
    showPeppolCheckbox() {
      return !this.isQuotation && ['NL', 'BE'].includes(this.currentCompany.country) 
      && (this.currentCompany.companysettings.is_peppol_company_authorized || (this.invoice.peppolinvoice && this.invoice.peppolinvoice.status))
    },
    isRecurrent() {
      return this.invoice && this.invoice.status === 'recurrent'
    },
    isUploaded() {
      return this.invoice && this.invoice.uploaded
    },
    attachedDocuments() {
      return this.quotationFiles
    },
    isEUR() {
      return this.invoice.currency === 'EUR'
    },
    isPaid() {
      return this.invoice.amount_to_be_paid && this.invoice.amount_to_be_paid == 0 && this.invoice.total_amount > 0
    },
    totalAmountEUR() {
      return this.$options.filters.currencyString(this.invoice.total_amount, 'EUR')
    },
    totalAmountCurrency() {
      return this.$options.filters.currencyString(this.invoice.currency_total_amount, this.invoice.currency)
    },
    amountToBePaidEUR() {
      return this.$options.filters.currencyString(this.invoice.amount_to_be_paid, 'EUR')
    },
    totalAmount() {
      let total_amount = 0

      if (this.isEUR) {
        total_amount = this.totalAmountEUR
      } else {
        if (this.invoice.total_amount && this.invoice.total_amount > 0) {
          total_amount = this.totalAmountCurrency + ' <span>(' + this.totalAmountEUR + ')</span>'
        } else {
          total_amount = this.totalAmountCurrency
        }
      }

      return total_amount
    },
    pendingAmount() {
      let pending_amount = 0
      if (this.isEUR) {
        pending_amount = this.amountToBePaidEUR
      } else {
        if (this.invoice.total_amount > 0) {
          pending_amount = this.amountToBePaidEUR
        } else {
          pending_amount = this.totalAmountCurrency
        }
      }

      return pending_amount
    },
    verifactuCheckboxDisabled() {
      return (this.invoice && this.invoice.verifactuinvoice && !['draft', 'unmarked'].includes(this.invoice.verifactuinvoice.status) 
        || (this.invoice && this.invoice.status !== 'd') || !this.invoice)
    },
    peppolCheckboxDisabled() {
      return (this.invoice.peppolinvoice && this.invoice.peppolinvoice.status && this.invoice.peppolinvoice.status !== 'draft') || this.invoice.status !== 'd'
    },
    verifactuTooltipTitle() {
      if (this.verifactuCheckboxDisabled) {
        if (this.isVerifactuInvoiceModel) {
          return this.$gettext('You cannot unmark the invoice as Verifactu because it has already been issued to the tax authorities.')
        } else {
          return this.$gettext('You cannot mark the invoice as Verifactu because the invoice has already been sent.')
        }
      } else {
        if (this.isVerifactuInvoiceModel) {
          return this.$gettext('Unmarking the invoice as Verifactu will prevent it from being issued to the tax authorities.')
        } else {
            return this.$gettext('Marking the invoice as Verifactu will ensure it is issued to the tax authorities when sent.')
        }
      }
    },
    peppolTooltipTitle() {
      if (this.peppolCheckboxDisabled) {
        if (this.isPeppolInvoiceModel) {
          return this.$gettext('You cannot unmark the invoice as PEPPOL because it has already been ') + (this.invoice.status === 'recurrent' ? this.$gettext('scheduled') : this.$gettext('issued to the recipient')) + '.'
        } else {
          return this.$gettext('You cannot mark the invoice as PEPPOL because the invoice has already been ') + (this.invoice.status === 'recurrent' ? this.$gettext('scheduled') : this.$gettext('sent')) + '.'
        }
      } else {
        if (this.isPeppolInvoiceModel) {
          return this.$gettext('Unmarking the invoice as PEPPOL will prevent it from being issued to the recipient via PEPPOL.')
        } else {
            return this.$gettext('Marking the invoice as PEPPOL will ensure it is issued to the recipient via PEPPOL when sent.')
        }
      }
    },
    isVATExempt () {
      return this.invoice && this.invoice.invoice_lines.some(line => line.vat_exempt)
    },
    isCredit() {
      return this.invoice && this.invoice.credited_invoice
    },
    showCancelVerifactudButton() {
      return this.invoice && (this.invoice.status === 's' || this.invoice.status === 'p') && this.invoice.verifactuinvoice && 
        (this.invoice.verifactuinvoice.status == 'correct' || this.invoice.verifactuinvoice.status == 'partially_correct' || 
        this.invoice.verifactuinvoice.status == 'cancellation_incorrect' || this.invoice.verifactuinvoice.status == 'error_cancelling')
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.showInvoicesModal = to.meta.showInvoicesModal
        this.modalType = to.meta.modalType
      },
    },
  },
  created() {
    this.invoiceId = this.isQuotation ? this.$route.params['quotationId'] : this.$route.params['invoiceId']
    this.previewPDFId = this.isQuotation ? this.$route.params['quotationId'] : this.$route.params['invoiceId']
    if (this.invoiceId) {
      this.setupData()
    }
  },
  methods: {
    ...mapActions({
      setInvoiceToCopy: 'invoices/setInvoiceToCopy',
      loadInvoice: "invoices/loadInvoice"
    }),
    ...mapMutations({
      setInvoicePDF: 'invoices/SET_INVOICE_PDF',
    }),
    setupData() {
      if (this.invoice) {
        if (!this.isRecurrent) {
          this.$emit('display-buttons', 'view')
        } else {
          this.$emit('display-buttons', 'view-recurrent')
        }
        
        if (this.invoice.verifactuinvoice) {
          this.$store.commit('invoices/SET_IS_VERIFACTU_INVOICE', !['unmarked', 'unmarked_sent'].includes(this.invoice.verifactuinvoice.status))
        } else if (this.currentCompany.country === 'ES' && !this.invoice.verifactuinvoice) {
          // if the default settting is set to true, the verifactuinvioce object will already be created
          this.$store.commit('invoices/SET_IS_VERIFACTU_INVOICE', false)
        }
        
        if (this.invoice.peppolinvoice && this.invoice.peppolinvoice.status) {
          this.$store.commit('invoices/SET_IS_PEPPOL_INVOICE', true)
        } else if (!this.invoice.peppolinvoice) {
          this.$store.commit('invoices/SET_IS_PEPPOL_INVOICE', false)
        }
      } else {
        console.log('Invoice is not set')
      }
      this.loading = false
    },
    closeInvoicesModal() {
      this.showInvoicesModal = false
      this.modalType = null
      this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.invoiceId } })
    },
    closeAdjustModal() {
      this.showAdjustModal = false
    },
    closeDesignModal() {
      this.showDesign = false
    },
    refreshPDF() {
      console.log('refreshPDF')
      this.showDesign = false
      this.setInvoicePDF(null)
      this.previewPDFId++ // to refresh PDF file after layout update
    },
    goToSubscriptions() {
      window.location.href = window.Urls['subscriptions:manage']()
    },
    downloadDocument(item) {
      DocumentService.getDocumentBlob(item.id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading Document:', error));
    },
    markAsVerifactu() {
      InvoiceService.markAsVerifactu(this.invoice.id, {mark_as_verifactu: this.isVerifactuInvoice}).then((response) => {
        if (response.status === 200) {
          this.$store.commit('invoices/SET_IS_VERIFACTU_INVOICE', !['unmarked', 'unmarked_sent'].includes(response.data.status))
          this.loadInvoice(this.invoice.id)
          this.refreshPDF()
        } else if (response.status === 208) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Change not applied'),
            text: response.data,
            variant: 'warning',
            duration: 4
          })
        }
      }).catch((error) => {
        if ([400, 409, 490, 491].includes(error.status)) {
          if (error.status === 491) {
            this.eInvoiceMsgActionType = 'verifactu_settings'
            this.showEInvoiceMessageModal = true
            this.eInvoiceMessageTitle = this.$gettext('Upload certificate')
            this.eInvoiceMessageText = this.$gettext('To send Verifactu invoices to the AEAT, please upload a digital certificate. You can do this in your account settings.')
          } else if (error.status === 490) { 
            this.eInvoiceMsgActionType = 'verifactu_settings'
            this.showEInvoiceMessageModal = true
            this.eInvoiceMessageTitle = this.$gettext('Country not supported')
            this.eInvoiceMessageText = this.$gettext('Only Spanish companies can send Verifactu invoices.')
          } else {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: error.data,
              variant: 'danger',
              duration: 4
            })
          }
        } else {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: this.$gettext('An error occurred while marking the invoice as Verifactu'),
            variant: 'danger',
            duration: 4
          })
        }
      })
    },
    markAsPeppol() {
      this.loadingPeppolRequest = true
      InvoiceService.markAsPeppol(this.invoice.id, {mark_as_peppol: this.isPeppolInvoice}).then((response) => {
        if (response.status === 200) {
          this.$store.commit('invoices/SET_IS_PEPPOL_INVOICE', response.data.peppolinvoice.status !== null)
          this.loadInvoice(this.invoice.id)
          this.refreshPDF()
        } else if (response.status === 208) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Change not applied'),
            text: response.data,
            variant: 'warning',
            duration: 4
          })
        }
      }).catch((error) => {
        if (error.status === 491) {
          this.eInvoiceMsgActionType = 'connect_peppol'
          this.showEInvoiceMessageModal = true
          this.eInvoiceMessageTitle = this.$gettext('Connect your company')
          this.eInvoiceMessageText = this.$gettext('To send Peppol invoices, you must connect first your company to the Peppol network.')
        } else if ([400, 409].includes(error.status)) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: error.data,
            variant: 'danger',
            duration: 4
          })
        } else {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: this.$gettext('An error occurred while marking the invoice as Peppol.'),
            variant: 'danger',
            duration: 4
          })
        }
      }).finally(() => {
        this.loadingPeppolRequest = false
      })
    },
    onVerifactuInvoiceCancelled() {
      InvoiceService.cancelVerifactuInvoice(this.invoice.id).then(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext('Invoice cancelled'),
          text: this.$gettext('The invoice was cancelled successfully in the Tax Authorities system'),
          variant: 'primary',
          duration: 4
        })
        this.showCancelVerifactudModal = false
        this.$emit('refresh-list')
      }).catch((error) => {
        if (error.status === 406) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice not cancelled'),
            text: this.$gettext('Your digital certificate is no longer stored in Gekko. Please re-upload your certificate to cancel the invoice.'),
            variant: 'danger',
            duration: 4
          })
        } else {
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice not cancelled'),
            text: this.$gettext('An error occurred while cancelling the invoice'),
            variant: 'danger',
            duration: 4
          })
        }
      })
    },
    onEInvoiceMessageClose() {
      this.showEInvoiceMessageModal = false
      this.eInvoiceMessageTitle = ''
      this.eInvoiceMessageText = ''
      this.showAdjustModal = false
    }
  }
}
</script>
<style scoped lang="scss">
.modal-invoice-view-wrap {
  height: 100%;
}
</style>

<style lang="scss">
.invoice-verifactu-switch-wrap {
  display: flex;
  align-items: center;
  .invoice-verifactu-switch { min-height: 20px; }
  .invoice-verifactu-switch-label {
    padding-right: 1rem;
    img {
      cursor: pointer;
      height: 32px;
      filter: grayscale(1);
      opacity: 0.5;
      transition: filter, opacity 0.3s ease;
    }
  }
  &.verifactu-active {
    .invoice-verifactu-switch-label {
      padding-right: 1rem;
      img {
        filter: grayscale(0);
        opacity: 1;
      }
    }
  }
}

.invoice-peppol-switch-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  .invoice-peppol-switch {
    min-height: 20px;
    padding-bottom: 10px;
  }
  .invoice-peppol-switch-label {
    padding-right: 1rem;
    img {
      cursor: pointer;
      height: 32px;
      filter: grayscale(1);
      opacity: 0.5;
      transition: filter, opacity 0.3s ease;
    }
  }
  &.peppol-active {
    .invoice-peppol-switch-label {
      padding-right: 1rem;
      img {
        filter: grayscale(0);
        opacity: 1;
      }
    }
  }
}
</style>